import React, { useCallback, useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, ClockIcon, ScaleIcon, PlusIcon, MinusIcon, RepeatIcon, ZapIcon, HelpCircleIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { shuffleOptions } from '../../../utils/deterministicShuffle';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { MULTIPLE_CHOICE_MODES, readMode } from '../../../content/questions/questionModes';
// Icon map for `icon_chips` mode. Keys are the lucide icon names that seeds may
// reference via data.optionIcons. Unknown names fall back to HelpCircleIcon.
const ICON_CHIP_MAP: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  ArrowRight: ArrowRightIcon,
  Clock: ClockIcon,
  Scale: ScaleIcon,
  Plus: PlusIcon,
  Minus: MinusIcon,
  Repeat: RepeatIcon,
  Zap: ZapIcon
};
function resolveChipIcon(name: string | undefined): ComponentType<SVGProps<SVGSVGElement>> {
  if (!name) return HelpCircleIcon;
  return ICON_CHIP_MAP[name] ?? HelpCircleIcon;
}
export function MultipleChoiceRenderer({
  question,
  onAnswer,
  onReadyChange,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  // Mode detection — read from question.data (seed-controlled). Default behaviour
  // is preserved for any seed that does NOT set data.mode. Unknown modes
  // intentionally fall through to default rendering (governance pipeline
  // catches them with TYPE_INVALID_MODE).
  const mode = readMode(question, MULTIPLE_CHOICE_MODES);
  const isIconChips = mode === 'icon_chips';
  const isTwoStepJudge = mode === 'two_step_judge';
  const optionIcons: string[] = useMemo(() => {
    const data = (question as {
      data?: unknown;
    }).data as Record<string, unknown> | undefined | null;
    const raw = data?.optionIcons;
    return Array.isArray(raw) ? raw as string[] : [];
  }, [question]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
    iconName?: string;
  }>>([]);
  useEffect(() => {
    const shuffled = shuffleOptions(question.options, question.id);
    // Attach optionIcons[originalIndex] so icons survive the shuffle.
    const withIcons = shuffled.map(opt => ({
      ...opt,
      iconName: optionIcons[opt.originalIndex]
    }));
    setShuffledOptions(withIcons);
    setSelectedOption(null);
  }, [question.id, question.options, optionIcons]);
  useEffect(() => {
    onReadyChange?.(selectedOption !== null);
  }, [selectedOption, onReadyChange]);
  const handleSelect = useCallback((index: number) => {
    setSelectedOption(index);
    if (shuffledOptions[index]) {
      onAnswer(shuffledOptions[index].originalIndex);
    }
  }, [shuffledOptions, onAnswer]);
  // Keyboard: 1-9 keys for quick selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = parseInt(e.key);
      if (!isNaN(key) && key >= 1 && key <= 9) {
        const index = key - 1;
        if (index < shuffledOptions.length) {
          handleSelect(index);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shuffledOptions, handleSelect]);
  const isSelected = (idx: number) => selectedOption === idx;
  // ─── Mode: two_step_judge ────────────────────────────────────────────────
  // Two-stage MC (TAA Q4 idiom_literal_judge). Step 1 binary judge; if the
  // learner picks the trigger index, step 2 reveals. Final answer payload
  // is JSON: { step1, step2? }.
  if (isTwoStepJudge) {
    return <TwoStepJudgeView question={question} color={color} onAnswer={onAnswer} onReadyChange={onReadyChange} data-id="element-8667" />;
  }
  // ─── Mode: icon_chips ────────────────────────────────────────────────────
  // Renders options as a responsive grid of icon-chip cards. Used by
  // signaalwoord-functie-style questions where each option is a discourse
  // function (oorzaak-gevolg, tijd, tegenstelling, toevoeging, …).
  if (isIconChips) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4" data-id="element-8668">
        {shuffledOptions.map((option, idx) => {
        const Icon = resolveChipIcon(option.iconName);
        const selected = isSelected(idx);
        return <motion.button key={option.originalIndex} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + idx * 0.08
        }} whileHover={{
          y: -2
        }} whileTap={{
          scale: 0.97
        }} onClick={() => handleSelect(idx)} className={`
                relative flex flex-col items-center justify-center gap-2 p-4 sm:p-5 rounded-2xl border-2 text-center font-medium transition-all duration-200 min-h-[120px]
                ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}
              `} style={selected ? {
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.08),
          color: color,
          boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`
        } as TailwindCSSProperties : undefined} onMouseEnter={e => {
          if (!selected) {
            e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
          }
        }} onMouseLeave={e => {
          if (!selected) {
            e.currentTarget.style.borderColor = '';
          }
        }} aria-pressed={selected} data-id="element-8669">
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition-colors ${selected ? '' : 'bg-slate-100 text-slate-500'}`} style={selected ? {
            backgroundColor: color,
            color: '#ffffff'
          } : undefined} data-id="element-8670">
                <Icon className="w-6 h-6" strokeWidth={2.25} data-id="element-8671" />
              </span>
              <span className="text-sm sm:text-base leading-tight" data-id="element-8672">
                <LatexRenderer content={option.text} data-id="element-8673" />
              </span>
              {selected && <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white" style={{
            backgroundColor: color
          }} data-id="element-8674">
                  <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-8675" />
                </span>}
              {idx < 9 && <span className="absolute bottom-2 right-2 hidden sm:inline-flex bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold text-slate-400" data-id="element-8676">
                  {idx + 1}
                </span>}
            </motion.button>;
      })}
      </div>;
  }
  // ─── Default mode: stacked option rows (unchanged behaviour) ─────────────
  return <div className="grid grid-cols-1 gap-4" data-id="element-8677">
      {shuffledOptions.map((option, idx) => <motion.button key={option.originalIndex} initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.3 + idx * 0.1
    }} whileHover={{
      scale: 1.01,
      x: 4
    }} whileTap={{
      scale: 0.99
    }} onClick={() => handleSelect(idx)} className={`
            p-5 rounded-2xl text-left text-lg font-medium transition-all duration-200 border-2 min-h-[72px] flex items-center group relative
            ${isSelected(idx) ? 'shadow-md ring-2 ring-offset-2' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}
          `} style={isSelected(idx) ? {
      borderColor: color,
      backgroundColor: hexToRgba(color, 0.08),
      color: color,
      boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`,
      outlineColor: hexToRgba(color, 0.3),
      '--tw-ring-color': hexToRgba(color, 0.3)
    } as TailwindCSSProperties : {
      '--hover-border': hexToRgba(color, 0.4)
    } as React.CSSProperties} onMouseEnter={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
      }
    }} onMouseLeave={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = '';
      }
    }} data-id="element-8678">
          <motion.span animate={{
        scale: isSelected(idx) ? [1, 1.3, 1] : 1
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className={`
              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-4 transition-colors
              ${!isSelected(idx) ? 'bg-slate-100 text-slate-500' : ''}
            `} style={isSelected(idx) ? {
        backgroundColor: color,
        color: '#ffffff'
      } : undefined} onMouseEnter={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = hexToRgba(color, 0.15);
          (e.currentTarget as HTMLElement).style.color = color;
        }
      }} onMouseLeave={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = '';
          (e.currentTarget as HTMLElement).style.color = '';
        }
      }} data-id="element-8679">
            {isSelected(idx) ? <CheckIcon className="w-4 h-4" strokeWidth={3} data-id="element-8680" /> : String.fromCharCode(65 + idx)}
          </motion.span>
          <span className="flex-1" data-id="element-8681">
            <LatexRenderer content={option.text} data-id="element-8682" />
          </span>
          {idx < 9 && <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-1 shadow-sm" data-id="element-8683">
              <kbd className="font-mono" data-id="element-8684">{idx + 1}</kbd>
            </span>}
        </motion.button>)}
    </div>;
}
// ─── two_step_judge view ───────────────────────────────────────────────────
// Encapsulated as a sub-component so it has its own state machine without
// disturbing the default/icon_chips render path.
interface StepConfig {
  prompt?: string;
  options: string[];
  correctIndex: number;
}
function TwoStepJudgeView({
  question,
  color,
  onAnswer,
  onReadyChange
}: {
  question: QuestionRendererProps['question'];
  color: string;
  onAnswer: QuestionRendererProps['onAnswer'];
  onReadyChange?: QuestionRendererProps['onReadyChange'];
}) {
  const data = ((question as any).data ?? {}) as Record<string, unknown>;
  const step1 = data.step1 as StepConfig | undefined;
  const step2 = data.step2 as StepConfig | undefined;
  const step2Trigger = typeof data.step2_ifIndex === 'number' ? data.step2_ifIndex : 1;
  const [step1Pick, setStep1Pick] = useState<number | null>(null);
  const [step2Pick, setStep2Pick] = useState<number | null>(null);
  // Reset when question changes
  useEffect(() => {
    setStep1Pick(null);
    setStep2Pick(null);
  }, [question.id]);
  const showStep2 = step1Pick === step2Trigger && !!step2;
  // Ready when:
  // - step1 chosen AND it does NOT trigger step2 (single-step path), OR
  // - step1 triggers step2 AND step2 chosen.
  const ready = step1Pick !== null && (showStep2 ? step2Pick !== null : true);
  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);
  // Keyboard: 1-9 keys for quick selection. Routes to step1 until it's
  // picked; once step2 is open, routes to step2 instead.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = parseInt(e.key);
      if (isNaN(key) || key < 1 || key > 9) return;
      const idx = key - 1;
      if (showStep2 && step2) {
        if (idx < step2.options.length) setStep2Pick(idx);
      } else if (step1) {
        if (idx < step1.options.length) {
          setStep1Pick(idx);
          if (idx !== step2Trigger) setStep2Pick(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showStep2, step1, step2, step2Trigger]);
  // Emit a structured answer once ready. We encode as JSON so the answer
  // protocol can record both picks; correctness is judged by validator.
  // Guard against duplicate emits (re-renders with identical payload) so
  // usePracticeSession sees one canonical answer per definitive choice.
  const lastEmittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || step1Pick === null) {
      lastEmittedRef.current = null;
      return;
    }
    const payload = JSON.stringify({
      step1: step1Pick,
      ...(showStep2 && step2Pick !== null ? {
        step2: step2Pick
      } : {})
    });
    if (lastEmittedRef.current === payload) return;
    lastEmittedRef.current = payload;
    onAnswer(payload);
  }, [ready, step1Pick, step2Pick, showStep2, onAnswer]);
  if (!step1) {
    return <div className="text-sm text-slate-500 italic" data-id="element-8685">
        Vraag mist <code data-id="element-8686">data.step1</code> — kan niet renderen.
      </div>;
  }
  return <div className="flex flex-col gap-6" data-id="element-8687">
      {/* Step 1 — binary judge */}
      <div data-id="element-8688">
        {step1.prompt && <div id="two-step-judge-step1-label" className="mb-3 text-sm font-medium text-slate-600" data-id="element-8689">
            Stap 1 — {step1.prompt}
          </div>}
        <div role="radiogroup" aria-labelledby={step1.prompt ? 'two-step-judge-step1-label' : undefined} aria-label={step1.prompt ? undefined : 'Stap 1 — kies een oordeel'} className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-8690">
          {step1.options.map((label, idx) => {
          const selected = step1Pick === idx;
          return <motion.button key={idx} whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.97
          }} onClick={() => {
            setStep1Pick(idx);
            if (idx !== step2Trigger) setStep2Pick(null);
          }} className={`relative p-5 sm:p-6 rounded-2xl border-2 text-base sm:text-lg font-semibold transition-all min-h-[88px] ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}`} style={selected ? {
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.08),
            color: color,
            boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`
          } as TailwindCSSProperties : undefined} role="radio" aria-checked={selected} data-id="element-8691">
                <LatexRenderer content={label} data-id="element-8692" />
                {selected && <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white" style={{
              backgroundColor: color
            }} data-id="element-8693">
                    <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-8694" />
                  </span>}
              </motion.button>;
        })}
        </div>
      </div>

      {/* Step 2 — meaning MC, only when triggered */}
      <AnimatePresence data-id="element-8695">
        {showStep2 && step2 && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25
      }} data-id="element-8696">
            <div id="two-step-judge-step2-label" className="mb-3 text-sm font-medium text-slate-600" data-id="element-8697">
              Stap 2 — {step2.prompt ?? 'Wat betekent het?'}
            </div>
            <div role="radiogroup" aria-labelledby="two-step-judge-step2-label" className="grid grid-cols-1 gap-3" data-id="element-8698">
              {step2.options.map((label, idx) => {
            const selected = step2Pick === idx;
            return <motion.button key={idx} whileHover={{
              x: 2
            }} whileTap={{
              scale: 0.99
            }} onClick={() => setStep2Pick(idx)} className={`p-4 rounded-xl border-2 text-left text-base font-medium transition-all min-h-[60px] flex items-center ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`} style={selected ? {
              borderColor: color,
              backgroundColor: hexToRgba(color, 0.08),
              color: color
            } as TailwindCSSProperties : undefined} role="radio" aria-checked={selected} data-id="element-8699">
                    <span className="flex-1" data-id="element-8700">
                      <LatexRenderer content={label} data-id="element-8701" />
                    </span>
                    {selected && <CheckIcon className="w-4 h-4 ml-3" strokeWidth={3} style={{
                color
              }} data-id="element-8702" />}
                  </motion.button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}